import { addToast, Modal } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { BoxMultiple } from '../../../../components/Box/BoxMultiple';
import { TranslationsKeys } from '../../../../locales/translations';
import { createStudentRemovalRequest } from '../api';
import { StudentsRemovalRequestCreateRequest } from '../types/students';
import { StudentsRemovalRequestCreateLoader } from './StudentsRemovalRequestCreateLoader';
import { StudentsRemovalRequestForm } from './StudentsRemovalRequestForm';

type StudentsRemovalRequestCreateModalProps = {
  studyPlanEnrollmentId: number;
  onClose: (success: boolean) => void;
};

export function StudentsRemovalRequestCreateModal({
  studyPlanEnrollmentId,
  onClose,
}: StudentsRemovalRequestCreateModalProps) {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_STUDENTS,
  );

  const texts = useMemo(
    () => ({
      title: t('students.createModal.title'),
      description: t('students.createModal.description'),
      studentName: t('students.createModal.studentName'),
      studentIdentification: t('students.createModal.studentIdentification'),
    }),
    [t],
  );

  const [submitting, setSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleCreate = useCallback(
    async (payload: StudentsRemovalRequestCreateRequest) => {
      setSubmitting(true);
      const { error } = await createStudentRemovalRequest(
        studyPlanEnrollmentId,
        payload,
      );
      setSubmitting(false);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('create.failed'),
        });
        return;
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: t(`create.success`),
      });

      onClose(true);
    },
    [onClose, t, studyPlanEnrollmentId],
  );

  return (
    <Modal isOpen size="md" toggle={handleClose} closeOnBackdrop={!submitting}>
      <StudentsRemovalRequestCreateLoader
        studyPlanEnrollmentId={studyPlanEnrollmentId}
      >
        {({ data }) => (
          <>
            <Row>
              <Col xs={12}>
                <h1 className="text-dark text-center fw-600 fs-20">
                  {texts.title}
                </h1>
                <p className=" text-center fs-16 mb-4">{texts.description}</p>
              </Col>
            </Row>
            <div className="mb-4">
              <BoxMultiple
                content={[
                  {
                    title: texts.studentName,
                    body: `${data.student.accountNames} ${data.student.accountLastNames}`,
                  },
                  {
                    title: texts.studentIdentification,
                    body: `${data.student.identificationTypeName} ${data.student.identificationValueFormatted}`,
                  },
                ]}
              />
            </div>
            <StudentsRemovalRequestForm
              studyPlanVersionName={data.studyPlanVersion.name}
              types={data.types}
              onSubmit={handleCreate}
              onCancel={handleClose}
            />
          </>
        )}
      </StudentsRemovalRequestCreateLoader>
    </Modal>
  );
}
